import {
  clearMenuSubText,
  getListsForManySubcategories,
  getSubcategoryListNextPage,
  setMenuSubText,
  setVideoOptions,
  setVodAsset,
  Translation
} from 'common/actions';
import { DefaultButton } from 'common/components/button';
import { VODCard } from 'common/components/cards';
import Card from 'common/components/cards/Card';
import { CategoryHorizontalList } from 'common/components/category-horizontal-list';
import { Column } from 'common/components/common/style';
import OrderSelectorHorizontalView, {
  OrderId
} from 'common/components/order-selector-horizontal-view/OrderSelectorHorizontalView';
import { Stripe } from 'common/components/stripe';
import StripeViewSkeleton from 'common/components/stripe-view-skeleton/StripeViewSkeleton';
import { H1, H3 } from 'common/components/Typography';
import { ViewWithDetails } from 'common/components/view-with-details';
import { ROUTE_VOD } from 'common/config/constants';
import variables from 'common/config/variables';
import {
  CategoriesOptions,
  Category,
  Epg,
  EpgsStore,
  IViewWithDetailsStripeItem,
  LiveChannels
} from 'common/constants/data-types';
import { LoadMoreOptions } from 'common/interfaces';
import AbortHandler from 'common/services/AbortHandler';
import { getChannelLogo } from 'common/services/helpers';
import { getScalablePixel } from 'common/utils';
import {
  useOnlineStatus,
  usePageNavigation,
  useParentalLock,
  usePlayerActions
} from 'common/utils/hooks';
import { isEmpty, isNil } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useLocation, useParams } from 'react-router';
const { DEFAULT_PAGE_SIZE } = variables;

interface StateProps {
  i18n: Translation;
  recorded: EpgsStore['recorded'];
  getListsForManySubcategories: (category: Category, vod?: boolean) => Promise<void>;
  clearMenuSubText: VoidFunction;
  getSubcategoryListNextPage: (
    id: number | string,
    count?: number,
    offset?: number,
    vod?: boolean
  ) => Promise<any>;
  setMenuSubText: (text: string | string[]) => void;
  setVodAsset: (asset: Epg) => void;
  setVideoOptions: any;
  liveChannels: LiveChannels;
  logos: any;
  isPlaying: boolean;
  categoriesOptions?: CategoriesOptions;
}

interface ArchiveProps {
  vod?: boolean;
}

function CategoryArchive(props: ArchiveProps & StateProps) {
  const location = useLocation();
  const { locked: channelsLocked } = useParentalLock();
  const { category: categoryId, subcategory: subcategoryId } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [order, setOrder] = React.useState<OrderId>('date-added');
  const [isOnline] = useOnlineStatus();
  const { playVideoFromContinueWatching: playVideo } = usePlayerActions();

  const { categories, subcategoriesEpgs } = useMemo(() => {
    const subscategoriesEpgs = Object.entries(props.recorded?.subcategoriesEpgs).map(
      ([subCatId, epgs]) => {
        return [
          subCatId,
          // Filter out locked channels
          (epgs as Epg[]).filter((e) => (channelsLocked ? e.visible : true))
        ];
      }
    );
    //
    return {
      ...props.recorded,
      subcategoriesEpgs: Object.fromEntries(subscategoriesEpgs)
    };
  }, [props.recorded, channelsLocked, location.pathname]);

  const emptyEpgs = useMemo(
    () => !loading && isEmpty(Object.values(subcategoriesEpgs).flat()),
    [subcategoriesEpgs, loading]
  );
  const navigate = usePageNavigation();
  const currentCategory = useMemo(
    () => props.recorded && categoryId && props.recorded[categoryId],
    [props.recorded, categoryId]
  );

  const subcategory = useMemo(() => {
    if (isNil(categoryId) || isNil(subcategoryId)) {
      return undefined;
    }
    return categories[parseInt(categoryId)]?.subcategories?.find(
      (e) => e.id === parseInt(subcategoryId)
    );
  }, [categoryId, subcategoryId, categories]);

  const orderedSubcategoryEpgs = useMemo(() => {
    if (!subcategory || !subcategoriesEpgs || !subcategoriesEpgs[subcategory.id]) {
      return undefined;
    }
    const epgs = (subcategoriesEpgs[subcategory.id] as Epg[])?.filter((e) =>
      props.vod ? !!e.epgs?.length : true
    );
    // Order
    const orderedEpgs = epgs.sort((a, b) => {
      switch (order) {
        case 'rating':
          return b.rating.Overall - a.rating.Overall;
        case 'title':
          return a.title.localeCompare(b.title);
        case 'date-added':
          return b.addts - a.addts;
        case 'year':
          return (a.releaseDate || '').localeCompare(b.releaseDate || '');
        default:
          return 0;
      }
    });
    return orderedEpgs;
    //
  }, [subcategory, subcategoriesEpgs, order, props.vod]);

  useEffect(() => {
    if (!isEmpty(categories) && categoryId) {
      props.setMenuSubText([categories[categoryId].name, subcategory?.name]);
    }
    return () => {
      props.clearMenuSubText();
    };
  }, [subcategory, categories, categoryId]);

  useEffect(() => {
    setLoading(true);
    isOnline &&
      !isEmpty(categories) &&
      categoryId &&
      props
        .getListsForManySubcategories(categories[categoryId], props.vod)
        .then((data?: unknown) => data && setLoading(false));
    return () => {
      AbortHandler.abort();
    };
  }, [isOnline, categories, categoryId, props.vod]);

  const goBack = useCallback(() => {
    navigate(-1, false);
  }, [navigate]);

  const openPlayer = useCallback(
    (epg: Epg) => {
      if (props.vod) {
        props.setVodAsset(epg);
        return;
      }
      playVideo(epg, { isFullscreen: true, continueWatching: true });
    },
    [props.vod]
  );

  const renderItem = useCallback(
    (item: Epg, index: number) => {
      if (props.vod) {
        return (
          <VODCard
            key={`vod-card-${item.eventid}`}
            epg={item}
            showTitle={props.categoriesOptions?.[item.majorId]?.SHOW_CATEGORY_CARD_TITLE}
            testId={`category-archive-vod-card-${index}`}
          />
        );
      }
      return (
        <Card
          key={`archive-card-${item.eventid}`}
          epg={item}
          logo={getChannelLogo(item.chan_id, props.logos)}
          testId={`category-archive-epg-card-${index}`}
          i18n={props.i18n}
        />
      );
    },
    [props.logos, props.vod, props.categoriesOptions, props.i18n]
  );

  const loadMore = useCallback(
    ({ rowIndex }: LoadMoreOptions) => {
      try {
        if (isNil(categoryId)) {
          return;
        }
        const { subcategories } = categories[parseInt(categoryId)];
        const currentCategory = subcategories[rowIndex].id;
        const offset = (subcategoriesEpgs[currentCategory] as Epg[]).length;
        props.getSubcategoryListNextPage(currentCategory, DEFAULT_PAGE_SIZE, offset, props.vod);
      } catch (error) {
        console.warn(error);
      }
    },
    [categories, categoryId, subcategoriesEpgs, props.vod]
  );

  const getFocusKeyOnVodCategory = useCallback((item: Category, index: number) => {
    return `vod-item-${item.id}-${index}`;
  }, []);

  const getFocusKey = useCallback(
    (_item: Epg, index: number) => {
      return `ordered-vod-${index}-${order}`;
    },
    [order]
  );

  const onSubcategorySelect = useCallback((mainCategory: Category, category: Category) => {
    navigate(`${ROUTE_VOD}/${mainCategory.id}/${category.id}`);
  }, []);

  const renderStripes = useCallback(() => {
    if (loading || isNil(categoryId)) {
      return;
    }

    // Render grid
    if (orderedSubcategoryEpgs) {
      return (
        <Stripe
          key={subcategoryId}
          getFocusKey={getFocusKey}
          title={
            <OrderSelectorHorizontalView
              categoryId={categoryId}
              order={order}
              onOrderSelect={setOrder}
              vod={props.vod}
            />
          }
          items={orderedSubcategoryEpgs}
          loadMore={loadMore}
          onStripeItemPress={openPlayer}
          renderItem={renderItem}
          scrollPadding={-variables.STRIPE_PADDING}
          grid
          testId="category-grid-stripe"
        />
      );
    }

    // Render stripes
    return Object.values(categories[parseInt(categoryId)].subcategories).flatMap((row) => {
      // Filter out empty epgs on VOD items
      const epgs = (subcategoriesEpgs[row.id] as Epg[])?.filter((e) =>
        props.vod ? !!e.epgs?.length : true
      );
      // Skip empty subcategories
      if (isEmpty(epgs)) {
        return [];
      }

      return (
        <Stripe
          key={row.id}
          title={
            props.vod ? (
              <CategoryHorizontalList
                category={row}
                getFocusKey={getFocusKeyOnVodCategory}
                onMainCategorySelect={(category) =>
                  onSubcategorySelect(categories[parseInt(categoryId)], category)
                }
              />
            ) : (
              row.name
            )
          }
          items={epgs}
          loadMore={loadMore}
          onStripeItemPress={openPlayer}
          renderItem={renderItem}
          offsetBottom={props.vod ? variables.STRIPE_OFFSET_BOTTOM_PADDING : undefined}
          testId={`subcategory-epg-stripe-${row.name}`}
        />
      );
    });
  }, [
    categories,
    subcategoriesEpgs,
    categoryId,
    loading,
    currentCategory,
    subcategory,
    order,
    props.vod,
    getFocusKey
  ]);

  const onBack = useCallback((last?: boolean) => {
    if (last) {
      goBack();
      return false;
    }
    return true;
  }, []);

  const getSelectedAssetData = useCallback(
    (asset: IViewWithDetailsStripeItem) => {
      if (props.vod) {
        const firstEpg = asset.epgs?.[0];
        return {
          epg: {
            ...asset,
            vod: true,
            year: asset.year || asset.releaseDate?.split('-')[0],
            duration: asset.duration || firstEpg?.duration || ''
          },
          resolution: firstEpg?.profiles?.[0]?.resolution,
          thumbnailUrl: asset.photos.find((photo) => photo.type === 'cover')?.url
        };
      }

      if (asset.chan_id) {
        return {
          epg: asset,
          // Extract resolution from liveChannels
          resolution: props.liveChannels[asset.chan_id]?.resolution
        };
      }
      return undefined;
    },
    [props.liveChannels, props.vod]
  );

  const renderEmptyState = useCallback(() => {
    return (
      <Column
        flex="1 0 80%"
        justifyContent="center"
        alignItems="center"
        maxWidth="40%"
        alignSelf="center"
        textAlign="center"
        gap={getScalablePixel(16)}
      >
        <H1 style={{ fontSize: getScalablePixel(32) }}>
          {props.i18n?.errorPage?.restrictedAccessToCategory}
        </H1>
        <H3>{props.i18n?.errorPage?.restrictedAccessToCategoryDescription}</H3>
        <DefaultButton
          title={props.i18n?.common?.goBack}
          size="small"
          initiallyFocused
          onEnterPress={goBack}
        />
      </Column>
    );
  }, [props.i18n, goBack]);

  return (
    <>
      <ViewWithDetails
        key={location.pathname}
        focusKey={location.pathname}
        getSelectedAssetData={getSelectedAssetData}
        initiallyFocused={!loading}
        saveFocusOnUnmount={false}
        onBack={onBack}
        loading={loading}
        loader={loading && <StripeViewSkeleton />}
        emptyState={renderEmptyState()}
        hideBanners={emptyEpgs}
      >
        {renderStripes()}
      </ViewWithDetails>
    </>
  );
}

const mapStateToProps = (
  { videoPlayer, epgs, liveChannels, logos, i18n }: any,
  ownProps: ArchiveProps
) => ({
  i18n,
  liveChannels,
  isPlaying: videoPlayer.isPlaying,
  recorded: epgs[ownProps.vod ? 'vod' : 'recorded'],
  logos,
  categoriesOptions: epgs[ownProps.vod ? 'vod' : 'recorded']?.categoriesOptions
});

export default React.memo(
  connect(mapStateToProps, {
    setMenuSubText,
    clearMenuSubText,
    getListsForManySubcategories,
    getSubcategoryListNextPage,
    setVodAsset,
    setVideoOptions
  })(CategoryArchive)
);
